import { useState, useEffect, useCallback, useRef, type ReactNode } from "react"
import { useParams, useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"
import {
  useQuizzes,
  useQuizVersions,
  useCreateQuizVersion,
  useUpdateQuizVersion,
  usePublishQuizVersion,
  useDeleteQuizVersion,
  useQuestions,
  useCreateQuestion,
  useUpdateQuestion,
  useDeleteQuestion,
  useReorderQuestions,
  useCurrentAdmin,
  useQuizFeedback,
} from "@/lib/queries"
import type { QuizVersion, Question, QuestionType, AnswerOption, QuizFeedbackRow, VersionLock } from "@/lib/types"
import { hasPermission, PERMISSIONS } from "@/lib/permissions"
import { api, ApiError } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  FileCheck,
  ChevronUp,
  ChevronDown,
  Loader2,
  Star,
} from "lucide-react"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusLabel(status: QuizVersion["status"]) {
  if (status === "draft") return "Draft"
  if (status === "published") return "Published"
  return "Archived"
}

function versionLabel(v: QuizVersion) {
  return `${v.label} – ${statusLabel(v.status)}`
}

// ---------------------------------------------------------------------------
// QuizEditorPage
// ---------------------------------------------------------------------------

export function QuizEditorPage() {
  const { id } = useParams({ strict: false })
  const navigate = useNavigate()
  const quizId = parseInt(id as string)

  const { data: quizzes = [] } = useQuizzes()
  const quiz = quizzes.find((q) => q.id === quizId) ?? null

  const { data: versions = [], isLoading: versionsLoading } = useQuizVersions(
    quizId || null
  )

  const createQuizVersion = useCreateQuizVersion(quizId || null)
  const updateQuizVersion = useUpdateQuizVersion()
  const publishQuizVersion = usePublishQuizVersion()
  const deleteQuizVersion = useDeleteQuizVersion()

  const { data: currentAdminData } = useCurrentAdmin()
  const canManage = hasPermission(currentAdminData, PERMISSIONS.MANAGE_QUIZZES)

  const { data: feedbackRows = [], isLoading: feedbackLoading } = useQuizFeedback(
    canManage ? (quizId || null) : null
  )

  const [outerTab, setOuterTab] = useState<"versions" | "feedback">("versions")

  // Selected version tab: prefer draft, then published
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null)

  // UI state
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)
  const [deleteDraftConfirmOpen, setDeleteDraftConfirmOpen] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [createVersionError, setCreateVersionError] = useState<string | null>(null)

  // Auto-select a version when versions load
  useEffect(() => {
    if (versions.length === 0) {
      setSelectedVersionId(null)
      return
    }
    const draft = versions.find((v) => v.status === "draft")
    const published = versions.find((v) => v.status === "published")
    const preferred = draft ?? published ?? versions[0]
    setSelectedVersionId((prev) => {
      // If the previously selected version still exists, keep it
      if (prev && versions.find((v) => v.id === prev)) return prev
      return preferred.id
    })
  }, [versions])

  const selectedVersion = versions.find((v) => v.id === selectedVersionId) ?? null
  const isDraft = selectedVersion?.status === "draft"

  // Editable version fields (controlled)
  const [editLabel, setEditLabel] = useState("")

  useEffect(() => {
    if (selectedVersion) {
      setEditLabel(selectedVersion.label)
    }
  }, [selectedVersion?.id])

  const handleSaveLabel = async () => {
    if (!selectedVersion || !isDraft) return
    const label = editLabel.trim()
    if (label === "" || label === selectedVersion.label) {
      setEditLabel(selectedVersion.label)
      return
    }
    try {
      await updateQuizVersion.mutateAsync({ id: selectedVersion.id, label })
    } catch (err) {
      console.error("Failed to update version:", err)
      setEditLabel(selectedVersion.label)
    }
  }

  const handleCreateDraft = async () => {
    setCreateVersionError(null)
    try {
      const newVersion = await createQuizVersion.mutateAsync()
      setSelectedVersionId(newVersion.id)
    } catch (err) {
      if (err instanceof ApiError) {
        setCreateVersionError(err.message)
      } else {
        console.error("Failed to create draft:", err)
      }
    }
  }

  const handlePublish = async () => {
    if (!selectedVersion) return
    setPublishError(null)
    try {
      await publishQuizVersion.mutateAsync(selectedVersion.id)
      setPublishConfirmOpen(false)
    } catch (err) {
      if (err instanceof ApiError) {
        setPublishError(err.message)
      } else {
        console.error("Failed to publish:", err)
      }
    }
  }

  const handleDeleteDraft = async () => {
    if (!selectedVersion) return
    try {
      await deleteQuizVersion.mutateAsync({ id: selectedVersion.id, quizId })
      setDeleteDraftConfirmOpen(false)
      setSelectedVersionId(null)
    } catch (err) {
      console.error("Failed to delete draft:", err)
    }
  }

  // Determine tab value for the version selector
  const draftVersion = versions.find((v) => v.status === "draft")
  const publishedVersion = versions.find((v) => v.status === "published")
  const archivedVersions = versions
    .filter((v) => v.status === "archived")
    .sort((a, b) => {
      const aDate = a.archived_at ?? ""
      const bDate = b.archived_at ?? ""
      return bDate.localeCompare(aDate)
    })

  const tabCount =
    (draftVersion ? 1 : 0) +
    (publishedVersion ? 1 : 0) +
    (archivedVersions.length > 0 ? 1 : 0)

  // Advisory editing lock on the draft (same pattern as the document editor).
  const [lock, setLock] = useState<VersionLock | null>(null)
  const holdsLock = lock?.mine === true
  // Tracks whether the previous lock response said we held the lock, so the
  // heartbeat can announce the moment someone takes the lock away from us.
  const hadLockRef = useRef(false)

  const applyLockState = useCallback((next: VersionLock) => {
    if (hadLockRef.current && !next.mine && next.locked_by) {
      toast.warning(
        `${next.locked_by.email} took over editing this assessment. Your unsaved changes are still on screen — copy anything you need.`,
        { duration: 10000 }
      )
    }
    hadLockRef.current = next.mine
    setLock(next)
  }, [])

  const claimLock = useCallback(
    async (versionId: number, force = false) => {
      try {
        const result = await api.post<VersionLock>(
          `/api/v1/admin/quiz_versions/${versionId}/lock`,
          force ? { force: true } : undefined
        )
        applyLockState(result)
      } catch (error) {
        if (error instanceof ApiError && error.status === 423) {
          applyLockState(error.data as VersionLock)
        } else {
          console.error("Failed to claim editing lock:", error)
        }
      }
    },
    [applyLockState]
  )

  // Claim the lock while a draft exists; the heartbeat keeps it fresh and
  // re-claims automatically once the other admin's lock is released or expires.
  // ponytail: no idle auto-release like the document editor — "Take over"
  // covers an abandoned tab; add the idle timer if that bites in practice.
  useEffect(() => {
    if (!draftVersion || !canManage) {
      hadLockRef.current = false
      setLock(null)
      return
    }
    const versionId = draftVersion.id
    claimLock(versionId)

    const release = () => {
      const csrf = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")
      fetch(`/api/v1/admin/quiz_versions/${versionId}/lock`, {
        method: "DELETE",
        keepalive: true,
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : {},
      }).catch(() => {})
    }

    const interval = setInterval(() => claimLock(versionId), 10_000)
    window.addEventListener("beforeunload", release)
    return () => {
      clearInterval(interval)
      window.removeEventListener("beforeunload", release)
      release()
      hadLockRef.current = false
      setLock(null)
    }
  }, [draftVersion?.id, canManage, claimLock])

  const lockBanner: ReactNode =
    lock && !lock.mine && lock.locked_by ? (
      <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-700 dark:bg-amber-950">
        <span>
          {lock.locked_by.email} is editing this draft
          {lock.locked_at &&
            ` (since ${new Date(lock.locked_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })})`}
          . The draft is read-only until they finish their changes, or you take
          over.
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => draftVersion && claimLock(draftVersion.id, true)}
        >
          Take over
        </Button>
      </div>
    ) : null

  const activeTab =
    selectedVersionId === draftVersion?.id
      ? "draft"
      : selectedVersionId === publishedVersion?.id
        ? "published"
        : archivedVersions.find((v) => v.id === selectedVersionId)
          ? "history"
          : draftVersion
            ? "draft"
            : "published"

  const handleTabChange = (tab: string) => {
    if (tab === "draft" && draftVersion) setSelectedVersionId(draftVersion.id)
    else if (tab === "published" && publishedVersion)
      setSelectedVersionId(publishedVersion.id)
    else if (tab === "history" && archivedVersions.length > 0)
      setSelectedVersionId(archivedVersions[0].id)
  }

  if (!quiz && quizzes.length > 0) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center text-muted-foreground">Assessment not found.</div>
      </div>
    )
  }

  if (!quiz) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center text-muted-foreground">
          Loading assessment...
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <Button
        variant="ghost"
        onClick={() => navigate({ to: "/admin/content", search: { tab: "assessments" } })}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Assessments
      </Button>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{quiz.title}</h1>
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              {quiz.has_published && (
                <span className="text-lime-600 font-medium">Published</span>
              )}
              {quiz.has_draft && (
                <span className="text-orange-500 font-medium">Draft</span>
              )}
              {!quiz.has_published && !quiz.has_draft && (
                <span>No Version</span>
              )}
            </div>
            {createVersionError && (
              <p className="text-sm text-destructive mt-2">{createVersionError}</p>
            )}
          </div>

          {outerTab === "versions" && (
            <div className="flex gap-2">
              {/* Show "New Draft" button when there is a published version but no draft */}
              {canManage && !quiz.has_draft && quiz.has_published && (
                <Button
                  onClick={handleCreateDraft}
                  disabled={createQuizVersion.isPending}
                  variant="outline"
                >
                  {createQuizVersion.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Pencil className="h-4 w-4 mr-2" />
                  )}
                  New Draft
                </Button>
              )}
              {/* Create first draft when no versions exist */}
              {canManage && !quiz.has_draft && !quiz.has_published && (
                <Button
                  onClick={handleCreateDraft}
                  disabled={createQuizVersion.isPending}
                >
                  {createQuizVersion.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Create First Draft
                </Button>
              )}
              {/* Draft controls */}
              {quiz.has_draft && selectedVersion?.status === "draft" && (
                <>
                  {canManage && (
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteDraftConfirmOpen(true)}
                      disabled={!holdsLock}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Draft
                    </Button>
                  )}
                  {canManage && (
                    <Button
                      onClick={() => {
                        setPublishError(null)
                        setPublishConfirmOpen(true)
                      }}
                      disabled={!holdsLock}
                    >
                      <FileCheck className="h-4 w-4 mr-2" />
                      Publish
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <Separator />

        {/* Outer tabs: Versions | Feedback (feedback gated on canManage) */}
        {canManage ? (
          <Tabs value={outerTab} onValueChange={(v) => setOuterTab(v as "versions" | "feedback")}>
            <TabsList>
              <TabsTrigger value="versions">Versions</TabsTrigger>
              <TabsTrigger value="feedback">
                Feedback
                {feedbackRows.length > 0 && (
                  <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums">
                    {feedbackRows.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="versions" className="mt-4">
              <VersionsContent
                versionsLoading={versionsLoading}
                versions={versions}
                tabCount={tabCount}
                selectedVersion={selectedVersion}
                isDraft={isDraft}
                canManage={canManage}
                holdsLock={holdsLock}
                lockBanner={lockBanner}
                editLabel={editLabel}
                setEditLabel={setEditLabel}
                handleSaveLabel={handleSaveLabel}
                activeTab={activeTab}
                handleTabChange={handleTabChange}
                draftVersion={draftVersion}
                publishedVersion={publishedVersion}
                archivedVersions={archivedVersions}
              />
            </TabsContent>

            <TabsContent value="feedback" className="mt-4">
              <FeedbackList rows={feedbackRows} isLoading={feedbackLoading} />
            </TabsContent>
          </Tabs>
        ) : (
          <VersionsContent
            versionsLoading={versionsLoading}
            versions={versions}
            tabCount={tabCount}
            selectedVersion={selectedVersion}
            isDraft={isDraft}
            canManage={canManage}
            holdsLock={holdsLock}
            lockBanner={lockBanner}
            editLabel={editLabel}
            setEditLabel={setEditLabel}
            handleSaveLabel={handleSaveLabel}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            draftVersion={draftVersion}
            publishedVersion={publishedVersion}
            archivedVersions={archivedVersions}
          />
        )}
      </div>

      {/* Publish confirm dialog */}
      {canManage && (
        <AlertDialog
          open={publishConfirmOpen}
          onOpenChange={(open) => {
            setPublishConfirmOpen(open)
            if (!open) setPublishError(null)
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Publish Assessment Version</AlertDialogTitle>
              <AlertDialogDescription>
                This will make this version live. Every question must have at
                least one correct answer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {publishError && (
              <p className="text-sm text-destructive px-1">{publishError}</p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={publishQuizVersion.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault()
                  handlePublish()
                }}
                disabled={publishQuizVersion.isPending}
              >
                {publishQuizVersion.isPending ? "Publishing..." : "Publish"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Delete draft confirm dialog */}
      {canManage && (
        <AlertDialog
          open={deleteDraftConfirmOpen}
          onOpenChange={setDeleteDraftConfirmOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Draft</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the current draft and all its
                questions.
                {quiz.has_published
                  ? " The published version will not be affected."
                  : " This assessment will have no versions."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteDraft}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteQuizVersion.isPending}
              >
                {deleteQuizVersion.isPending ? "Deleting..." : "Delete Draft"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// VersionsContent — the versions tab body (extracted to avoid duplication)
// ---------------------------------------------------------------------------

interface VersionsContentProps {
  versionsLoading: boolean
  versions: QuizVersion[]
  tabCount: number
  selectedVersion: QuizVersion | null
  isDraft: boolean
  canManage: boolean
  holdsLock: boolean
  lockBanner: ReactNode
  editLabel: string
  setEditLabel: (v: string) => void
  handleSaveLabel: () => void
  activeTab: string
  handleTabChange: (tab: string) => void
  draftVersion: QuizVersion | undefined
  publishedVersion: QuizVersion | undefined
  archivedVersions: QuizVersion[]
}

function VersionsContent({
  versionsLoading,
  versions,
  tabCount,
  selectedVersion,
  isDraft,
  canManage,
  holdsLock,
  lockBanner,
  editLabel,
  setEditLabel,
  handleSaveLabel,
  activeTab,
  handleTabChange,
  draftVersion,
  publishedVersion,
  archivedVersions,
}: VersionsContentProps) {
  if (versionsLoading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Loading...
      </div>
    )
  }
  if (versions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>This assessment has no versions yet.</p>
      </div>
    )
  }
  if (tabCount <= 1) {
    return (
      <div className="space-y-6">
        {isDraft && lockBanner}
        {selectedVersion && (
          <VersionEditor
            version={selectedVersion}
            isDraft={isDraft}
            canManage={isDraft ? canManage && holdsLock : canManage}
            editLabel={editLabel}
            onLabelChange={setEditLabel}
            onLabelBlur={handleSaveLabel}
          />
        )}
      </div>
    )
  }
  return (
    <Tabs value={activeTab} onValueChange={handleTabChange}>
      <TabsList>
        {draftVersion && (
          <TabsTrigger value="draft">Draft</TabsTrigger>
        )}
        {publishedVersion && (
          <TabsTrigger value="published">Published</TabsTrigger>
        )}
        {archivedVersions.length > 0 && (
          <TabsTrigger value="history">History</TabsTrigger>
        )}
      </TabsList>

      {draftVersion && (
        <TabsContent value="draft" className="mt-4 space-y-6">
          {lockBanner}
          <VersionEditor
            version={draftVersion}
            isDraft={true}
            canManage={canManage && holdsLock}
            editLabel={activeTab === "draft" ? editLabel : draftVersion.label}
            onLabelChange={setEditLabel}
            onLabelBlur={handleSaveLabel}
          />
        </TabsContent>
      )}

      {publishedVersion && (
        <TabsContent value="published" className="mt-4 space-y-6">
          <VersionEditor
            version={publishedVersion}
            isDraft={false}
            canManage={canManage}
            editLabel={publishedVersion.label}
            onLabelChange={() => {}}
            onLabelBlur={() => {}}
          />
        </TabsContent>
      )}

      {archivedVersions.length > 0 && (
        <TabsContent value="history" className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {archivedVersions.length} archived{" "}
            {archivedVersions.length === 1 ? "version" : "versions"}
          </p>
          {archivedVersions.map((v) => (
            <ArchivedVersionEntry key={v.id} version={v} />
          ))}
        </TabsContent>
      )}
    </Tabs>
  )
}

// ---------------------------------------------------------------------------
// FeedbackList — admin view of quiz feedback
// ---------------------------------------------------------------------------

function RatingDisplay({ rating }: { rating: number | null }) {
  if (rating === null) {
    return <span className="text-muted-foreground text-sm">No rating</span>
  }
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">{rating}/5</span>
    </div>
  )
}

function FeedbackList({
  rows,
  isLoading,
}: {
  rows: QuizFeedbackRow[]
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading feedback...
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground text-sm">
        No feedback yet.
      </div>
    )
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {rows.length} {rows.length === 1 ? "response" : "responses"}, newest first
      </p>
      {rows.map((row) => (
        <div key={row.id} className="rounded-lg border bg-card p-4 space-y-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <RatingDisplay rating={row.rating} />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>Version {row.version_label}</span>
              <span>
                {new Date(row.created_at).toLocaleDateString("en-CA", {
                  timeZone: "UTC",
                })}
              </span>
            </div>
          </div>
          {row.comment ? (
            <p className="text-sm">{row.comment}</p>
          ) : (
            <p className="text-sm text-muted-foreground italic">No comment</p>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// VersionEditor — version metadata + questions list
// ---------------------------------------------------------------------------

function VersionEditor({
  version,
  isDraft,
  canManage,
  editLabel,
  onLabelChange,
  onLabelBlur,
}: {
  version: QuizVersion
  isDraft: boolean
  canManage: boolean
  editLabel: string
  onLabelChange: (v: string) => void
  onLabelBlur: () => void
}) {
  const { data: questions = [], isLoading } = useQuestions(version.id)
  const createQuestion = useCreateQuestion(version.id)
  const updateQuestion = useUpdateQuestion()
  const deleteQuestion = useDeleteQuestion()
  const reorderQuestions = useReorderQuestions(version.id)

  const [addQuestionOpen, setAddQuestionOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null)

  const handleMoveQuestion = async (index: number, direction: "up" | "down") => {
    const newQuestions = [...questions]
    const targetIndex = direction === "up" ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newQuestions.length) return
    ;[newQuestions[index], newQuestions[targetIndex]] = [
      newQuestions[targetIndex],
      newQuestions[index],
    ]
    try {
      await reorderQuestions.mutateAsync(newQuestions.map((q) => q.id))
    } catch (err) {
      console.error("Failed to reorder questions:", err)
    }
  }

  const handleDeleteQuestion = async (q: Question) => {
    try {
      await deleteQuestion.mutateAsync({ id: q.id, versionId: version.id })
    } catch (err) {
      console.error("Failed to delete question:", err)
    }
  }

  return (
    <div className="space-y-6">
      {/* Version metadata */}
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
          Version Settings
        </h2>
        <div className="space-y-1.5">
          <Label htmlFor={`label-${version.id}`}>Version</Label>
          <Input
            id={`label-${version.id}`}
            value={editLabel}
            maxLength={20}
            placeholder="e.g. 2026.2"
            onChange={(e) => onLabelChange(e.target.value)}
            onBlur={onLabelBlur}
            onKeyDown={(e) => { if (e.key === "Enter") onLabelBlur() }}
            disabled={!isDraft || !canManage}
            className="w-40"
          />
        </div>
        {version.published_at && (
          <p className="text-xs text-muted-foreground">
            Published:{" "}
            {new Date(version.published_at).toLocaleDateString("en-CA", {
              timeZone: "UTC",
            })}
          </p>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">
            Questions
            {questions.length > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({questions.length})
              </span>
            )}
          </h2>
          {isDraft && canManage && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddQuestionOpen(true)}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Question
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading questions...
          </div>
        ) : questions.length === 0 ? (
          <div className="rounded-lg border border-dashed py-10 text-center text-muted-foreground text-sm">
            No questions yet.{" "}
            {isDraft && canManage && "Add a question to get started."}
          </div>
        ) : (
          <div className="space-y-2">
            {questions.map((q, index) => (
              <QuestionRow
                key={q.id}
                question={q}
                index={index}
                isFirst={index === 0}
                isLast={index === questions.length - 1}
                isDraft={isDraft}
                canManage={canManage}
                onMoveUp={() => handleMoveQuestion(index, "up")}
                onMoveDown={() => handleMoveQuestion(index, "down")}
                onEdit={() => setEditingQuestion(q)}
                onDelete={() => handleDeleteQuestion(q)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Question dialog */}
      {isDraft && canManage && (
        <QuestionFormDialog
          open={addQuestionOpen}
          onOpenChange={setAddQuestionOpen}
          versionId={version.id}
          onSave={async (data) => {
            await createQuestion.mutateAsync(data)
            setAddQuestionOpen(false)
          }}
          isSaving={createQuestion.isPending}
        />
      )}

      {/* Edit Question dialog */}
      {isDraft && canManage && editingQuestion && (
        <QuestionFormDialog
          open={!!editingQuestion}
          onOpenChange={(open) => { if (!open) setEditingQuestion(null) }}
          versionId={version.id}
          initialQuestion={editingQuestion}
          onSave={async (data) => {
            await updateQuestion.mutateAsync({
              id: editingQuestion.id,
              versionId: version.id,
              ...data,
            })
            setEditingQuestion(null)
          }}
          isSaving={updateQuestion.isPending}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// QuestionRow
// ---------------------------------------------------------------------------

function QuestionRow({
  question,
  index,
  isFirst,
  isLast,
  isDraft,
  canManage,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
}: {
  question: Question
  index: number
  isFirst: boolean
  isLast: boolean
  isDraft: boolean
  canManage: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const correctOption = question.answer_options.find((o) => o.correct)

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <span className="shrink-0 mt-0.5 text-sm font-medium text-muted-foreground w-6 text-right">
          {index + 1}.
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{question.prompt}</p>
          <div className="mt-2 space-y-1">
            {question.answer_options.map((opt) => (
              <div
                key={opt.id}
                className={`flex items-center gap-2 text-sm ${opt.correct ? "text-foreground font-medium" : "text-muted-foreground"}`}
              >
                <span
                  className={`h-3 w-3 shrink-0 rounded-full border ${opt.correct ? "bg-primary border-primary" : "border-muted-foreground"}`}
                />
                {opt.text}
                {opt.correct && (
                  <span className="text-xs text-lime-600">(correct)</span>
                )}
              </div>
            ))}
          </div>
          {!correctOption && (
            <p className="mt-2 text-xs text-destructive">
              No correct answer set
            </p>
          )}
        </div>

        {isDraft && canManage && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onMoveUp}
              disabled={isFirst}
              title="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onMoveDown}
              disabled={isLast}
              title="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onEdit}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => setDeleteConfirmOpen(true)}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDeleteConfirmOpen(false)
                onDelete()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// QuestionFormDialog — add or edit a question
// ---------------------------------------------------------------------------

interface DraftAnswerOption {
  id?: number
  text: string
  correct: boolean
  position: number
}

const DEFAULT_OPTION_COUNT = 4

function blankAnswerOptions(): DraftAnswerOption[] {
  return Array.from({ length: DEFAULT_OPTION_COUNT }, (_, i) => ({
    text: "",
    correct: false,
    position: i + 1,
  }))
}

function QuestionFormDialog({
  open,
  onOpenChange,
  versionId: _versionId,
  initialQuestion,
  onSave,
  isSaving,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  versionId: number
  initialQuestion?: Question
  onSave: (data: {
    prompt: string
    question_type: QuestionType
    answer_options?: DraftAnswerOption[]
    correct_text?: string
  }) => Promise<void>
  isSaving: boolean
}) {
  const isEditing = !!initialQuestion

  const [prompt, setPrompt] = useState("")
  const [questionType, setQuestionType] = useState<QuestionType>("single_choice")
  const [answerOptions, setAnswerOptions] = useState<DraftAnswerOption[]>(blankAnswerOptions)
  const [correctText, setCorrectText] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormError(null)
      if (initialQuestion) {
        setPrompt(initialQuestion.prompt)
        setQuestionType(initialQuestion.question_type)
        setAnswerOptions(
          initialQuestion.answer_options.map((o) => ({
            id: o.id,
            text: o.text,
            correct: o.correct,
            position: o.position,
          }))
        )
        setCorrectText(
          initialQuestion.question_type === "true_false"
            ? initialQuestion.answer_options.find((o) => o.correct)?.text ?? null
            : null
        )
      } else {
        setPrompt("")
        setQuestionType("single_choice")
        setAnswerOptions(blankAnswerOptions())
        setCorrectText(null)
      }
    }
  }, [open])

  const setCorrect = (index: number) => {
    setAnswerOptions((prev) =>
      prev.map((o, i) => ({ ...o, correct: i === index }))
    )
  }

  const updateOptionText = (index: number, text: string) => {
    setAnswerOptions((prev) =>
      prev.map((o, i) => (i === index ? { ...o, text } : o))
    )
  }

  const addOption = () => {
    setAnswerOptions((prev) => [
      ...prev,
      { text: "", correct: false, position: prev.length + 1 },
    ])
  }

  const removeOption = (index: number) => {
    if (answerOptions.length <= 2) return
    setAnswerOptions((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((o, i) => ({ ...o, position: i + 1 }))
    )
  }

  const handleSubmit = async () => {
    setFormError(null)
    if (!prompt.trim()) {
      setFormError("Question prompt is required.")
      return
    }

    let payload: {
      prompt: string
      question_type: QuestionType
      answer_options?: DraftAnswerOption[]
      correct_text?: string
    }

    if (questionType === "true_false") {
      if (!correctText) {
        setFormError("Choose which answer is correct.")
        return
      }
      payload = {
        prompt: prompt.trim(),
        question_type: questionType,
        correct_text: correctText,
      }
    } else {
      if (answerOptions.some((o) => !o.text.trim())) {
        setFormError("All answer options must have text.")
        return
      }
      if (!answerOptions.some((o) => o.correct)) {
        setFormError("Exactly one answer option must be marked correct.")
        return
      }
      payload = {
        prompt: prompt.trim(),
        question_type: questionType,
        answer_options: answerOptions.map((o, i) => ({
          ...(o.id !== undefined ? { id: o.id } : {}),
          text: o.text.trim(),
          correct: o.correct,
          position: i + 1,
        })),
      }
    }

    try {
      await onSave(payload)
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message)
      } else {
        console.error("Failed to save question:", err)
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Question" : "Add Question"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Question type */}
          <div className="space-y-1">
            <Label htmlFor="question-type">Question type</Label>
            <Select
              value={questionType}
              onValueChange={(v) => setQuestionType(v as QuestionType)}
              disabled={isEditing}
            >
              <SelectTrigger id="question-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single_choice">Multiple choice</SelectItem>
                <SelectItem value="true_false">True / False</SelectItem>
              </SelectContent>
            </Select>
            {isEditing && (
              <p className="text-xs text-muted-foreground">
                A question's type cannot be changed after it is created.
              </p>
            )}
          </div>

          {/* Prompt */}
          <div className="space-y-1.5">
            <Label htmlFor="question-prompt">Question</Label>
            <textarea
              id="question-prompt"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Enter the question prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>

          {/* Answer options */}
          {questionType === "true_false" ? (
            <div className="space-y-1">
              <Label>Correct answer</Label>
              <div className="flex gap-2">
                {["True", "False"].map((text) => (
                  <Button
                    key={text}
                    type="button"
                    size="sm"
                    variant={correctText === text ? "default" : "outline"}
                    aria-pressed={correctText === text}
                    onClick={() => setCorrectText(text)}
                  >
                    {text}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                True and False are provided automatically. Choose which one is correct.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Answer Options</Label>
              <p className="text-xs text-muted-foreground">
                Select the radio button next to the correct answer.
              </p>
              {answerOptions.map((opt, index) => (
                <div key={index} className="flex items-center gap-2">
                  {/* Radio for correct */}
                  <input
                    type="radio"
                    name="correct-answer"
                    checked={opt.correct}
                    onChange={() => setCorrect(index)}
                    className="h-4 w-4 shrink-0 accent-primary cursor-pointer"
                    aria-label={`Mark option ${index + 1} as correct`}
                  />
                  <Input
                    value={opt.text}
                    onChange={(e) => updateOptionText(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1"
                  />
                  {answerOptions.length > 2 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeOption(index)}
                      type="button"
                      title="Remove option"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={addOption}
                type="button"
                className="w-full"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Option
              </Button>
            </div>
          )}

          {formError && (
            <p className="text-sm text-destructive">{formError}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : isEditing ? "Save Changes" : "Add Question"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// ArchivedVersionEntry — collapsed view for archived quiz versions
// ---------------------------------------------------------------------------

function ArchivedVersionEntry({ version }: { version: QuizVersion }) {
  const [expanded, setExpanded] = useState(false)
  const { data: questions = [], isLoading } = useQuestions(
    expanded ? version.id : null
  )

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null
    return new Date(dateStr).toLocaleDateString("en-CA", { timeZone: "UTC" })
  }

  return (
    <div className="rounded-lg border bg-card">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors rounded-lg"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground rotate-[-90deg]" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium">{versionLabel(version)}</p>
          <div className="flex gap-4 text-xs text-muted-foreground mt-0.5">
            {version.published_at && (
              <span>Published {formatDate(version.published_at)}</span>
            )}
            {version.archived_at && (
              <span>Archived {formatDate(version.archived_at)}</span>
            )}
            <span>{version.question_count} questions</span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading questions...
            </div>
          ) : questions.length === 0 ? (
            <p className="text-center py-4 text-muted-foreground text-sm">
              No questions in this version.
            </p>
          ) : (
            <div className="space-y-3">
              {questions.map((q, index) => (
                <div key={q.id} className="text-sm">
                  <p className="font-medium">
                    {index + 1}. {q.prompt}
                  </p>
                  <ul className="mt-1 ml-4 space-y-0.5">
                    {q.answer_options.map((opt: AnswerOption) => (
                      <li
                        key={opt.id}
                        className={`flex items-center gap-1.5 ${opt.correct ? "font-medium text-foreground" : "text-muted-foreground"}`}
                      >
                        <span
                          className={`h-2.5 w-2.5 shrink-0 rounded-full border ${opt.correct ? "bg-primary border-primary" : "border-muted-foreground"}`}
                        />
                        {opt.text}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
